import React, { useState, useEffect } from 'react';
import { Users, Building2, Layers, Activity, TrendingUp, Eye, Clock, Package, Truck, AlertCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { dashboardService, purchaseService, listingsService } from '../../services';
import toast from 'react-hot-toast';

const Dashboard = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [stats, setStats] = useState(null);
	const [priorityItems, setPriorityItems] = useState(null);
	const [loading, setLoading] = useState(true);
	const [priorityLoading, setPriorityLoading] = useState(true);
	const [urgentShipOrders, setUrgentShipOrders] = useState([]);
	const [urgentShipLoading, setUrgentShipLoading] = useState(false);
	const [returnToSellerListings, setReturnToSellerListings] = useState([]);
	const [returnToSellerLoading, setReturnToSellerLoading] = useState(false);

	const normalizedRole = user?.role ? String(user.role).toLowerCase() : '';
	const isSuperAdmin = normalizedRole === 'super admin';
	const isStoreManager = normalizedRole === 'store manager';
	const isAdmin = normalizedRole === 'admin';
	const isCustomerService = normalizedRole === 'customer service';

	// Helper function to get full image URL
	const getImageUrl = (path) => {
		if (!path) return null;
		// If path already starts with http, return as is
		if (path.startsWith('http')) return path;
		// Otherwise prepend the storage base URL
		const baseUrl = process.env.REACT_APP_API_URL || 'http://carsandbids-api.test';
		return `${baseUrl}/storage/${path}`;
	};

	// Get dress image from listing
	const getDressImage = (listing) => {
		if (!listing) return null;
		
		// Try to get front_full image from dress_images array (handle both camelCase and snake_case)
		const dressImages = listing.dress_images || listing.dressImages || [];
		if (Array.isArray(dressImages) && dressImages.length > 0) {
			const frontImage = dressImages.find(img => img.image_type === 'front_full');
			if (frontImage) {
				return getImageUrl(frontImage.thumbnail_path || frontImage.medium_path || frontImage.original_path);
			}
			// If no front_full, use first image
			const firstImage = dressImages[0];
			return getImageUrl(firstImage.thumbnail_path || firstImage.medium_path || firstImage.original_path);
		}
		
		// Fallback to other image fields
		if (listing.front_image) return getImageUrl(listing.front_image);
		if (listing.frontFullImage) return getImageUrl(listing.frontFullImage);
		if (listing.itemDetails?.front_image) return getImageUrl(listing.itemDetails.front_image);
		
		return null;
	};

	const formatIndianCurrency = (value) => {
		if (value === null || value === undefined || value === '') return '₹0';
		const numericValue = Number(value);
		if (Number.isNaN(numericValue)) return '₹0';
		return `₹${numericValue.toLocaleString('en-IN')}`;
	};

	const formatDate = (dateValue) => {
		if (!dateValue) return 'N/A';
		const parsedDate = new Date(dateValue);
		if (Number.isNaN(parsedDate.getTime())) return 'N/A';
		return parsedDate.toLocaleDateString();
	};

	const isRejectedFeesPaidStatus = (listing) => {
		if (!listing) return false;

		const statusCandidates = [
			listing.status,
			listing.status_text,
			listing.dress_status_id,
			listing.dressStatusId
		];

		return statusCandidates.some((value) => {
			if (value === undefined || value === null) return false;
			const stringValue = String(value).toLowerCase();
			return stringValue === '8' ||
				stringValue === '10' ||
				stringValue === 'returned to seller' ||
				stringValue === 'returned-to-seller' ||
				stringValue === 'return dispatched' ||
				stringValue === 'return-dispatched';
		});
	};

	console.log('📊 Dashboard user data:', user);

	useEffect(() => {
		fetchStats();
		fetchPriorityItems();
		fetchUrgentShipOrders();
	}, []);

	useEffect(() => {
		if (isSuperAdmin || isStoreManager) {
			fetchReturnToSellerListings();
		} else {
			setReturnToSellerListings([]);
		}
	}, [isSuperAdmin, isStoreManager]);

	const fetchStats = async () => {
		try {
			setLoading(true);
			const response = await dashboardService.getStatistics();
			if (response.success) {
				setStats(response.data);
			}
		} catch (error) {
			console.error('Error fetching dashboard statistics:', error);
			// Don't show error toast, just use default values
		} finally {
			setLoading(false);
		}
	};

	const fetchPriorityItems = async () => {
		try {
			setPriorityLoading(true);
			const response = await dashboardService.getPriorityItems();
			if (response.success) {
				setPriorityItems(response.data);
			}
		} catch (error) {
			console.error('Error fetching priority items:', error);
		} finally {
			setPriorityLoading(false);
		}
	};

	const fetchUrgentShipOrders = async () => {
		// Customer Service role should not access ready-to-ship endpoint
		if (isCustomerService) {
			setUrgentShipOrders([]);
			setUrgentShipLoading(false);
			return;
		}
		
		try {
			setUrgentShipLoading(true);
			// Fetch from backend ready-to-ship endpoint
			const response = await purchaseService.getReadyToShip();
			if (response.success) {
				setUrgentShipOrders(Array.isArray(response.data) ? response.data : []);
			}
		} catch (error) {
			console.error('Error fetching urgent ship orders:', error);
			toast.error('Failed to fetch urgent ship orders');
		} finally {
			setUrgentShipLoading(false);
		}
	};

	const fetchReturnToSellerListings = async () => {
		try {
			setReturnToSellerLoading(true);
			const response = await listingsService.getAll({
				status: 8,
				per_page: 25,
			});

			if (response.success) {
				const listings = Array.isArray(response.data) ? response.data : [];

				const filteredListings = listings.filter((listing) => {
					const statusCandidates = [
						listing.status,
						listing.status_text,
						listing.dress_status_id,
						listing.dressStatusId,
					];

					const statusMatches = statusCandidates.some((value) => {
						if (value === undefined || value === null) return false;
						const stringValue = String(value).toLowerCase();
						return stringValue === '8' ||
							stringValue === '10' ||
							stringValue === 'returned to seller' ||
							stringValue === 'returned-to-seller' ||
							stringValue === 'return dispatched' ||
							stringValue === 'return-dispatched';
					});

					const dressMatches = statusCandidates.some((value) => {
						if (value === undefined || value === null) return false;
						const numericValue = Number(value);
						return !Number.isNaN(numericValue) && (numericValue === 8 || numericValue === 10);
					});

					const returnPayments = Array.isArray(listing.listing_return_payments) ? listing.listing_return_payments : [];
					const hasPaidReturn = returnPayments.some((payment) => {
						if (!payment || payment.status === undefined || payment.status === null) return false;
						return String(payment.status).toLowerCase() === 'paid';
					});

					return statusMatches && dressMatches && hasPaidReturn;
				});

				setReturnToSellerListings(filteredListings);
			} else {
				setReturnToSellerListings([]);
			}
		} catch (error) {
			console.error('Error fetching return to seller listings:', error);
			toast.error('Failed to fetch return to seller listings');
			setReturnToSellerListings([]);
		} finally {
			setReturnToSellerLoading(false);
		}
	};

	// Convert API stats to widget format
	const formatStatsForWidgets = (apiStats, role) => {
		if (!apiStats) return [];

		if (role === 'Super Admin') {
			return [
				{
					title: 'Total Users',
					value: apiStats.total_users?.toLocaleString() || '0',
					change: '+12%',
					changeType: 'positive',
					icon: Users,
					color: '#3b82f6'
				},
				{
					title: 'Active Branches',
					value: apiStats.active_branches?.toLocaleString() || '0',
					change: '+2',
					changeType: 'positive',
					icon: Building2,
					color: '#10b981'
				},
				{
					title: 'Categories',
					value: apiStats.categories?.toLocaleString() || '0',
					change: '+3',
					changeType: 'positive',
					icon: Layers,
					color: '#f59e0b'
				},
				{
					title: 'Active Listings',
					value: apiStats.active_listings?.toLocaleString() || '0',
					change: '+8%',
					changeType: 'positive',
					icon: Activity,
					color: '#8b5cf6'
				},
				{
					title: 'Today Sold',
					value: apiStats.today_sold?.toLocaleString() || '0',
					change: '+23%',
					changeType: 'positive',
					icon: TrendingUp,
					color: '#ef4444'
				},
				{
					title: 'Pending Listings',
					value: apiStats.pending_listings?.toLocaleString() || '0',
					change: '+5',
					changeType: 'positive',
					icon: Clock,
					color: '#f59e0b'
				},
				{
					title: 'Total Items in Store',
					value: apiStats.total_items_in_store?.toLocaleString() || '0',
					change: '+156',
					changeType: 'positive',
					icon: Package,
					color: '#06b6d4'
				}
			];
		} else if (role === 'Store Manager') {
			return [
				{
					title: 'Pending Shipments',
					value: apiStats.pending_shipments?.toLocaleString() || '0',
					change: '+3',
					changeType: 'positive',
					icon: Clock,
					color: '#f59e0b'
				},
				{
					title: 'In Transit',
					value: apiStats.in_transit?.toLocaleString() || '0',
					change: '+1',
					changeType: 'positive',
					icon: Truck,
					color: '#3b82f6'
				},
				{
					title: 'Delivered Today',
					value: apiStats.delivered_today?.toLocaleString() || '0',
					change: '+2',
					changeType: 'positive',
					icon: Package,
					color: '#10b981'
				},
				{
					title: 'Issues Reported',
					value: apiStats.issues_reported?.toLocaleString() || '0',
					change: '+1',
					changeType: 'negative',
					icon: AlertCircle,
					color: '#ef4444'
				},
				{
					title: 'Items Received',
					value: apiStats.items_received?.toLocaleString() || '0',
					change: '+18',
					changeType: 'positive',
					icon: Activity,
					color: '#8b5cf6'
				}
			];
		} else if (role === 'Authenticator') {
			return [
				{
					title: 'Pending Authentication',
					// Show dresses pending at store: dress_status_id == 5
					value: (apiStats.available_pending_store ?? apiStats.pending_authentication)?.toLocaleString() || '0',
					change: '+5',
					changeType: 'positive',
					icon: Clock,
					color: '#f59e0b'
				},
				{
					title: 'Authenticated Today',
					value: apiStats.authenticated_today?.toLocaleString() || '0',
					change: '+3',
					changeType: 'positive',
					icon: Activity,
					color: '#10b981'
				},
				{
					title: 'This Month',
					value: apiStats.authenticated_this_month?.toLocaleString() || '0',
					change: '+12',
					changeType: 'positive',
					icon: TrendingUp,
					color: '#8b5cf6'
				}
			];
		} else {
			// Default stats
			return [
				{
					title: 'Active Listings',
					value: apiStats.active_listings?.toLocaleString() || '0',
					change: '+8%',
					changeType: 'positive',
					icon: Activity,
					color: '#8b5cf6'
				},
				{
					title: 'Today Sold',
					value: apiStats.today_sold?.toLocaleString() || '0',
					change: '+23%',
					changeType: 'positive',
					icon: TrendingUp,
					color: '#ef4444'
				},
				{
					title: 'Pending Listings',
					value: apiStats.pending_listings?.toLocaleString() || '0',
					change: '+5',
					changeType: 'positive',
					icon: Clock,
					color: '#f59e0b'
				}
			];
		}
	};

	const widgets = formatStatsForWidgets(stats, user?.role);


	return (
		<div>
			{/* Welcome Section */}
			<div className="card" style={{ marginBottom: '24px' }}>
				<h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
					Welcome, {user?.name}
				</h1>
				<p style={{ color: '#6b7280', fontSize: '16px' }}>
					{user?.role === 'Super Admin' && 'Manage your business operations efficiently from this central hub.'}
					{user?.role === 'Store Manager' && 'Track shipments, deliveries, and store operations at a glance.'}
					{user?.role === 'Authenticator' && 'Authenticate items and manage quality control efficiently.'}
					{!user?.role || (user?.role !== 'Super Admin' && user?.role !== 'Store Manager' && user?.role !== 'Authenticator') && 'Overview of your activity and performance.'}
				</p>
			</div>

			{loading ? (
				<div style={{
					textAlign: 'center',
					padding: '60px 20px',
					color: '#6b7280',
					fontSize: '16px'
				}}>
					Loading dashboard statistics...
				</div>
			) : (
				<div style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
					gap: '14px',
					marginBottom: '32px'
				}}>
					{widgets.map((stat, index) => {
					const Icon = stat.icon;
					return (
						<div
							key={index}
							className="card"
							style={{
								background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
								border: '1px solid #e2e8f0',
								position: 'relative',
								overflow: 'hidden',
								padding: '16px'
							}}
						>
							<div style={{ position: 'relative', zIndex: 2 }}>
								<div style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'flex-start',
									marginBottom: '12px'
								}}>
									<div
										style={{
											width: '36px',
											height: '36px',
											borderRadius: '8px',
											backgroundColor: stat.color,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center'
										}}
									>
										<Icon size={18} color="white" />
									</div>
									<div style={{
										display: 'flex',
										alignItems: 'center',
										gap: '3px',
										color: stat.changeType === 'positive' ? '#10b981' : '#ef4444',
										fontSize: '12px',
										fontWeight: '600'
									}}>
										<TrendingUp size={12} />
										{stat.change}
									</div>
								</div>

								<h3 style={{
									fontSize: '24px',
									fontWeight: 'bold',
									color: '#111827',
									marginBottom: '2px'
								}}>
									{stat.value}
								</h3>

								<p style={{
									color: '#6b7280',
									fontSize: '12px',
									fontWeight: '500'
								}}>
									{stat.title}
								</p>
							</div>

							{/* Background decoration */}
							<div style={{
								position: 'absolute',
								top: '-20px',
								right: '-20px',
								width: '80px',
								height: '80px',
								borderRadius: '50%',
								backgroundColor: stat.color,
								opacity: 0.1,
								zIndex: 1
							}} />
						</div>
					);
				})}
			</div>
			)}

			{/* Urgent Shipping & Return to Seller Sections - Hidden for Customer Service */}
			{!isCustomerService && (isSuperAdmin || isStoreManager || isAdmin) && (
			<div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginTop: '32px' }}>
				<div className="card" style={{
					flex: '1 1 calc(50% - 12px)',
					maxWidth: 'calc(50% - 12px)',
					minWidth: '320px',
					borderLeft: '4px solid #ef4444',
					width: '50%'
				}}>
					<div style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: '16px'
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<AlertCircle size={20} color="#ef4444" />
							<h2 style={{
								fontSize: '20px',
								fontWeight: 'bold',
								color: '#ef4444',
								margin: 0
							}}>
								Urgent Need to Ship
							</h2>
						</div>
						<button
							onClick={() => navigate('/purchased-items')}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '4px',
								padding: '6px 12px',
								backgroundColor: '#4f46e5',
								color: 'white',
								border: 'none',
								borderRadius: '6px',
								cursor: 'pointer',
								fontSize: '12px',
								fontWeight: '500'
							}}
						>
							View All
							<ExternalLink size={14} />
						</button>
					</div>

					{urgentShipLoading ? (
						<div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
							Loading urgent orders...
						</div>
					) : urgentShipOrders.length > 0 ? (
						<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
							{urgentShipOrders.map((order, index) => (
								<div
									key={order.order_id || order.id || index}
									onClick={() => navigate(`/purchased-items?order=${order.order_id || order.id}`)}
									style={{
										padding: '16px',
										border: '1px solid #fecaca',
										borderRadius: '8px',
										marginBottom: '12px',
										cursor: 'pointer',
										transition: 'all 0.2s',
										display: 'flex',
										gap: '16px',
										alignItems: 'center',
										backgroundColor: '#fef2f2'
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.backgroundColor = '#fee2e2';
										e.currentTarget.style.borderColor = '#ef4444';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.backgroundColor = '#fef2f2';
										e.currentTarget.style.borderColor = '#fecaca';
									}}
								>
									{/* Order/Listing Image */}
									{getDressImage(order.listing) ? (
										<img
											src={getDressImage(order.listing)}
											alt={order.listing?.designer_brand || order.listing?.item_name || 'Order'}
											style={{
												width: '80px',
												height: '80px',
												objectFit: 'cover',
												borderRadius: '8px',
												border: '1px solid #fecaca'
											}}
											onError={(e) => {
												e.target.style.display = 'none';
												e.target.nextSibling.style.display = 'flex';
											}}
										/>
									) : null}
									<div style={{
										width: '80px',
										height: '80px',
										backgroundColor: '#fee2e2',
										borderRadius: '8px',
										display: getDressImage(order.listing) ? 'none' : 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										border: '1px solid #fecaca'
									}}>
										<Truck size={24} color="#ef4444" />
									</div>

									{/* Order Details */}
									<div style={{ flex: 1 }}>
										<div style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'start'
										}}>
											<div style={{ flex: 1 }}>
												<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
													<p style={{
														fontWeight: '600',
														color: '#111827',
														margin: 0,
														fontSize: '14px'
													}}>
														Order: {order.order_id || order.id || 'N/A'}
													</p>
													{(order.urgent_ship || (order.listing?.dress_status_id === 7 && order.listing?.status === 7)) && (
														<span style={{
															display: 'inline-block',
															padding: '2px 8px',
															borderRadius: '4px',
															fontSize: '10px',
															fontWeight: '600',
															backgroundColor: '#fee2e2',
															color: '#dc2626',
															border: '1px solid #fecaca'
														}}>
															⚠️ URGENT
														</span>
													)}
												</div>
												<p style={{
													fontWeight: '600',
													color: '#111827',
													margin: '0 0 4px 0',
													fontSize: '13px'
												}}>
													{order.listing?.designer_brand || order.listing?.itemDetails?.designer_brand || 'N/A'}
												</p>
												<p style={{
													color: '#6b7280',
													fontSize: '12px',
													margin: '0 0 4px 0'
												}}>
													{order.listing?.category || order.listing?.itemDetails?.category || 'N/A'}
												</p>
												<div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
													<p style={{
														color: '#6b7280',
														fontSize: '11px',
														margin: 0
													}}>
														<strong>Buyer:</strong> {order.buyer?.name || order.buyer?.full_name || 'N/A'}
													</p>
													{order.seller?.name || order.seller?.full_name ? (
														<p style={{
															color: '#6b7280',
															fontSize: '11px',
															margin: 0
														}}>
															<strong>Seller:</strong> {order.seller?.name || order.seller?.full_name}
														</p>
													) : null}
												</div>
											</div>
											<div style={{ textAlign: 'right', marginLeft: '16px' }}>
												<p style={{
													fontWeight: '600',
													color: '#059669',
													margin: '0 0 4px 0',
													fontSize: '14px'
												}}>
													₹{Number(order.total_amount || 0).toLocaleString('en-IN')}
												</p>
												<div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '4px' }}>
													<span style={{
														display: 'inline-block',
														padding: '2px 6px',
														borderRadius: '4px',
														fontSize: '10px',
														fontWeight: '500',
														backgroundColor: '#d1fae5',
														color: '#065f46'
													}}>
														25% Paid
													</span>
													<span style={{
														display: 'inline-block',
														padding: '2px 6px',
														borderRadius: '4px',
														fontSize: '10px',
														fontWeight: '500',
														backgroundColor: '#d1fae5',
														color: '#065f46'
													}}>
														75% Paid
													</span>
												</div>
												<p style={{
													color: '#9ca3af',
													fontSize: '10px',
													margin: '4px 0 0 0'
												}}>
													{new Date(order.created_at || order.updated_at).toLocaleDateString()}
												</p>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
							<Truck size={32} style={{ marginBottom: '8px', opacity: '0.5' }} />
							<p>No urgent orders to ship</p>
							<p style={{ fontSize: '12px', marginTop: '4px' }}>All orders have been shipped or are pending payment</p>
						</div>
					)}
				</div>

				{(isSuperAdmin || isStoreManager) && (
				<div className="card" style={{
					flex: '1 1 calc(50% - 12px)',
					maxWidth: 'calc(50% - 12px)',
					minWidth: '320px',
					borderLeft: '4px solid #7c3aed',
					width: '50%'
				}}>
					<div style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: '16px'
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Package size={20} color="#7c3aed" />
							<h2 style={{
								fontSize: '20px',
								fontWeight: 'bold',
								color: '#7c3aed',
								margin: 0,
								textTransform: 'capitalize'
							}}>
								return to seller after rejected
							</h2>
						</div>
						<button
							onClick={() => navigate('/listings?status=8')}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '4px',
								padding: '6px 12px',
								backgroundColor: '#7c3aed',
								color: 'white',
								border: 'none',
								borderRadius: '6px',
								cursor: 'pointer',
								fontSize: '12px',
								fontWeight: '500'
							}}
						>
							View Listings
							<ExternalLink size={14} />
						</button>
					</div>

					{returnToSellerLoading ? (
						<div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
							Loading return to seller listings...
						</div>
					) : returnToSellerListings.length > 0 ? (
						<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
							{returnToSellerListings.map((listing, index) => {
								const paidPayment = Array.isArray(listing.listing_return_payments)
									? listing.listing_return_payments.find((payment) => String(payment.status).toLowerCase() === 'paid')
									: null;
								const listingImage = getImageUrl(listing.front_image);

								return (
									<div
										key={listing.listing_id || index}
										onClick={() => listing.listing_id && navigate(`/listing-details/${listing.listing_id}`)}
										style={{
											padding: '16px',
											border: '1px solid #ddd6fe',
											borderRadius: '8px',
											marginBottom: '12px',
											cursor: listing.listing_id ? 'pointer' : 'default',
											transition: 'all 0.2s',
											display: 'flex',
											gap: '16px',
											alignItems: 'center',
											backgroundColor: '#f5f3ff'
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = '#ede9fe';
											e.currentTarget.style.borderColor = '#7c3aed';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = '#f5f3ff';
											e.currentTarget.style.borderColor = '#ddd6fe';
										}}
									>
										{listingImage ? (
											<img
												src={listingImage}
												alt={listing.listing_id || 'Listing'}
												style={{
													width: '80px',
													height: '80px',
													objectFit: 'cover',
													borderRadius: '8px',
													border: '1px solid #ddd6fe'
												}}
												onError={(e) => {
													e.currentTarget.style.display = 'none';
													const fallback = e.currentTarget.nextSibling;
													if (fallback) fallback.style.display = 'flex';
												}}
											/>
										) : null}
										<div style={{
											width: '80px',
											height: '80px',
											backgroundColor: '#ede9fe',
											borderRadius: '8px',
											display: listingImage ? 'none' : 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											border: '1px solid #ddd6fe'
										}}>
											<Package size={24} color="#7c3aed" />
										</div>

										<div style={{ flex: 1 }}>
											<div style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'flex-start',
												gap: '12px'
											}}>
												<div style={{ flex: 1 }}>
													<p style={{
														fontWeight: '600',
														color: '#111827',
														margin: '0 0 4px 0',
														fontSize: '14px'
													}}>
														Listing: {listing.listing_id || 'N/A'}
													</p>
													<p style={{
														color: '#6b7280',
														fontSize: '12px',
														margin: '0 0 4px 0'
													}}>
														Seller: {listing.username || listing.seller?.full_name || 'Unknown'}
													</p>
								<p style={{
									color: '#6b7280',
									fontSize: '11px',
									margin: 0
								}}>
									Status: {isRejectedFeesPaidStatus(listing) ? 'Rejection fees paid' : (listing.status_text || 'Returned to Seller')}
								</p>
												</div>
												<div style={{ textAlign: 'right' }}>
													<p style={{
														display: 'inline-block',
														padding: '2px 8px',
														borderRadius: '4px',
														fontSize: '11px',
														fontWeight: '600',
														backgroundColor: '#ddd6fe',
														color: '#5b21b6',
														margin: 0
													}}>
														{isRejectedFeesPaidStatus(listing) ? 'Rejection fees paid' : 'Returned'}
													</p>
												</div>
											</div>

											{paidPayment ? (
												<div style={{
													display: 'flex',
													flexWrap: 'wrap',
													gap: '12px',
													marginTop: '8px',
													fontSize: '11px',
													color: '#4338ca'
												}}>
													<span><strong>Paid Amount:</strong> {formatIndianCurrency(paidPayment.amount)}</span>
													<span><strong>Paid On:</strong> {formatDate(paidPayment.paid_at || paidPayment.updated_at)}</span>
													{paidPayment.payment_id ? (
														<span><strong>Payment ID:</strong> {paidPayment.payment_id}</span>
													) : null}
													{paidPayment.order_id ? (
														<span><strong>Order ID:</strong> {paidPayment.order_id}</span>
													) : null}
													{paidPayment.return_courier ? (
														<span><strong>Courier:</strong> {paidPayment.return_courier}</span>
													) : null}
													{paidPayment.return_awb ? (
														<span><strong>AWB:</strong> {paidPayment.return_awb}</span>
													) : null}
													{paidPayment.dispatched_at ? (
														<span><strong>Dispatched On:</strong> {formatDate(paidPayment.dispatched_at)}</span>
													) : null}
												</div>
											) : null}

											<p style={{
												color: '#9ca3af',
												fontSize: '10px',
												margin: '8px 0 0 0'
											}}>
												Returned on: {formatDate(listing.updated_at || listing.created_date)}
											</p>
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
							<Package size={32} style={{ marginBottom: '8px', opacity: '0.5' }} />
							<p>No listings awaiting seller return</p>
							<p style={{ fontSize: '12px', marginTop: '4px' }}>Rejected items with completed return payments will appear here</p>
						</div>
					)}
				</div>
				)}
			</div>
			)}

			{/* Priority Items Section - Only for Super Admin */}
			{user?.role === 'Super Admin' && (
				<div style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gap: '24px',
					marginTop: '32px'
				}}>
					{/* Left Column - Pending Listings */}
					<div>
					{/* Pending Listings */}
					<div className="card">
						<div style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: '16px'
						}}>
							<h2 style={{
								fontSize: '18px',
								fontWeight: 'bold',
								color: '#111827',
								margin: 0
							}}>
								Pending Listings
							</h2>
							<button
								onClick={() => navigate('/listings?status=1')}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
									padding: '6px 12px',
									backgroundColor: '#4f46e5',
									color: 'white',
									border: 'none',
									borderRadius: '6px',
									cursor: 'pointer',
									fontSize: '12px',
									fontWeight: '500'
								}}
							>
								View All
								<ExternalLink size={14} />
							</button>
						</div>

						{priorityLoading ? (
							<div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
								Loading...
							</div>
						) : priorityItems?.pending_listings?.length > 0 ? (
							<div style={{ maxHeight: '400px', overflowY: 'auto' }}>
								{priorityItems.pending_listings.map((listing, index) => (
									<div
										key={index}
										onClick={() => navigate(`/listing-details/${listing.listing_id}`)}
										style={{
											padding: '12px',
											border: '1px solid #e5e7eb',
											borderRadius: '8px',
											marginBottom: '8px',
											cursor: 'pointer',
											transition: 'all 0.2s',
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = '#f9fafb';
											e.currentTarget.style.borderColor = '#4f46e5';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = 'white';
											e.currentTarget.style.borderColor = '#e5e7eb';
										}}
									>
										<div style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'start'
										}}>
											<div style={{ flex: 1 }}>
												<p style={{
													fontWeight: '600',
													color: '#111827',
													margin: '0 0 4px 0',
													fontSize: '14px'
												}}>
													{listing.designer_brand}
												</p>
												<p style={{
													color: '#6b7280',
													fontSize: '12px',
													margin: '0 0 4px 0'
												}}>
													{listing.category}
												</p>
												<p style={{
													color: '#6b7280',
													fontSize: '11px',
													margin: 0
												}}>
													Seller: {listing.seller_name}
												</p>
											</div>
											<div style={{ textAlign: 'right' }}>
												<p style={{
													fontWeight: '600',
													color: '#059669',
													margin: '0 0 4px 0',
													fontSize: '13px'
												}}>
													₹{listing.ask_price?.toLocaleString('en-IN')}
												</p>
												<p style={{
													color: '#9ca3af',
													fontSize: '10px',
													margin: 0
												}}>
													{new Date(listing.created_at).toLocaleDateString()}
												</p>
											</div>
										</div>
									</div>
								))}
							</div>
						) : (
							<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
								<Clock size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
								<p>No pending listings</p>
							</div>
						)}
					</div>
					</div>

					{/* Right Column - Purchases Without AWB and Latest Offers */}
					<div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
					{/* Purchases Without AWB */}
					<div className="card">
						<div style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: '16px'
						}}>
							<h2 style={{
								fontSize: '18px',
								fontWeight: 'bold',
								color: '#111827',
								margin: 0
							}}>
								Purchases Without AWB
							</h2>
							<button
								onClick={() => navigate('/purchased-items')}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
									padding: '6px 12px',
									backgroundColor: '#4f46e5',
									color: 'white',
									border: 'none',
									borderRadius: '6px',
									cursor: 'pointer',
									fontSize: '12px',
									fontWeight: '500'
								}}
							>
								View All
								<ExternalLink size={14} />
							</button>
						</div>

						{priorityLoading ? (
							<div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
								Loading...
							</div>
						) : priorityItems?.purchases_without_awb?.length > 0 ? (
							<div style={{ maxHeight: '400px', overflowY: 'auto' }}>
								{priorityItems.purchases_without_awb.map((order, index) => (
									<div
										key={index}
										onClick={() => navigate(`/purchased-items?order=${order.order_id}`)}
										style={{
											padding: '12px',
											border: '1px solid #e5e7eb',
											borderRadius: '8px',
											marginBottom: '8px',
											cursor: 'pointer',
											transition: 'all 0.2s',
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = '#f9fafb';
											e.currentTarget.style.borderColor = '#4f46e5';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = 'white';
											e.currentTarget.style.borderColor = '#e5e7eb';
										}}
									>
										<div style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'start'
										}}>
											<div style={{ flex: 1 }}>
												<p style={{
													fontWeight: '600',
													color: '#111827',
													margin: '0 0 4px 0',
													fontSize: '14px'
												}}>
													{order.listing_name || 'N/A'}
												</p>
												<p style={{
													color: '#6b7280',
													fontSize: '12px',
													margin: '0 0 4px 0'
												}}>
													Order: {order.order_id}
												</p>
												<p style={{
													color: '#6b7280',
													fontSize: '11px',
													margin: 0
												}}>
													Buyer: {order.buyer_name || 'N/A'}
												</p>
											</div>
											<div style={{ textAlign: 'right' }}>
												<p style={{
													fontWeight: '600',
													color: '#059669',
													margin: '0 0 4px 0',
													fontSize: '13px'
												}}>
													₹{order.total_amount?.toLocaleString('en-IN')}
												</p>
												<span style={{
													display: 'inline-block',
													padding: '2px 6px',
													backgroundColor: '#fef3c7',
													color: '#92400e',
													borderRadius: '4px',
													fontSize: '10px',
													fontWeight: '500'
												}}>
													{order.status}
												</span>
											</div>
										</div>
									</div>
								))}
							</div>
						) : (
							<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
								<Package size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
								<p>All purchases have AWB numbers assigned</p>
							</div>
						)}
					</div>

					{/* Latest Offers */}
					<div className="card">
						<div style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: '16px'
						}}>
							<h2 style={{
								fontSize: '18px',
								fontWeight: 'bold',
								color: '#111827',
								margin: 0
							}}>
								Latest Offers
							</h2>
							<button
								onClick={() => navigate('/listings')}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
									padding: '6px 12px',
									backgroundColor: '#4f46e5',
									color: 'white',
									border: 'none',
									borderRadius: '6px',
									cursor: 'pointer',
									fontSize: '12px',
									fontWeight: '500'
								}}
							>
								View All
								<ExternalLink size={14} />
							</button>
						</div>

						{priorityLoading ? (
							<div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
								Loading...
							</div>
						) : priorityItems?.latest_offers?.filter(offer => offer.offer_status === 'pending')?.length > 0 ? (
							<div style={{ maxHeight: '400px', overflowY: 'auto' }}>
								{priorityItems.latest_offers.filter(offer => offer.offer_status === 'pending').map((offer, index) => (
									<div
										key={index}
										onClick={() => navigate(`/listing-details/${offer.listing_id}`)}
										style={{
											padding: '12px',
											border: '1px solid #e5e7eb',
											borderRadius: '8px',
											marginBottom: '8px',
											cursor: 'pointer',
											transition: 'all 0.2s',
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = '#f9fafb';
											e.currentTarget.style.borderColor = '#4f46e5';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = 'white';
											e.currentTarget.style.borderColor = '#e5e7eb';
										}}
									>
										<div style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'start'
										}}>
											<div style={{ flex: 1 }}>
												<p style={{
													fontWeight: '600',
													color: '#111827',
													margin: '0 0 4px 0',
													fontSize: '14px'
												}}>
													{offer.listing_name || 'N/A'}
												</p>
												<p style={{
													color: '#6b7280',
													fontSize: '12px',
													margin: '0 0 4px 0'
												}}>
													Buyer: {offer.buyer_name || 'N/A'}
												</p>
												{offer.offer_message && (
													<p style={{
														color: '#9ca3af',
														fontSize: '11px',
														margin: '4px 0 0 0',
														fontStyle: 'italic'
													}}>
														"{offer.offer_message.substring(0, 50)}{offer.offer_message.length > 50 ? '...' : ''}"
													</p>
												)}
											</div>
											<div style={{ textAlign: 'right' }}>
												<p style={{
													fontWeight: '600',
													color: '#3b82f6',
													margin: '0 0 4px 0',
													fontSize: '13px'
												}}>
													₹{offer.offer_amount?.toLocaleString('en-IN')}
												</p>
												<span style={{
													display: 'inline-block',
													padding: '2px 6px',
													backgroundColor: offer.offer_status === 'accepted' ? '#d1fae5' : '#dbeafe',
													color: offer.offer_status === 'accepted' ? '#065f46' : '#1e40af',
													borderRadius: '4px',
													fontSize: '10px',
													fontWeight: '500'
												}}>
													{offer.offer_status}
												</span>
											</div>
										</div>
									</div>
								))}
							</div>
						) : (
							<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
								<Package size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
								<p>No recent offers</p>
							</div>
						)}
					</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Dashboard;


